/**
 * Responsibility: build the project's mark as an element, in whatever size and colour the caller needs.
 *
 * The geometry is not here. It lives in `logo.svg`, which this module imports as text so there is exactly
 * one copy of the shape in the project — the same file the browser loads as the tab icon. Changing the
 * mark means editing that file and nothing else.
 *
 * Imported as text rather than fetched, so the mark is present the moment the menu is built. A `fetch`
 * would make the menu's construction asynchronous and leave a hole where the logo goes on the first
 * frame, and it would put the mark's URL — which the bundler rewrites and hashes — into runtime code.
 */

import logoMarkup from './logo.svg?raw';

/*
  Matches the stroke colour baked into the source file.

  The file has to name a real colour because a favicon is rendered standalone, with no page to inherit
  from: `currentColor` there resolves to black. So the file carries the indigo that suits a white tab
  strip, and anything drawing it against the game's black recolours it on the way through.
*/
const STROKE_PATTERN = /stroke="#[0-9A-Fa-f]{3,8}"/g;

/**
 * Build the logo as an inline SVG element.
 *
 * Inline rather than an `<img>`, for one reason: an `<img>` cannot be recoloured. The mark has to be
 * indigo in the tab and cyan in the menu, and inlining is what makes the same file able to be both.
 *
 * @param {object} [options]
 * @param {number} [options.size] - Rendered size in CSS pixels, square.
 * @param {string} [options.colour] - Stroke colour. Defaults to whatever the source file specifies.
 * @returns {SVGElement|null} The mark, or null if the source could not be parsed.
 */
export function createLogo({ size = 32, colour } = {}) {
  const markup = colour ? logoMarkup.replace(STROKE_PATTERN, `stroke="${colour}"`) : logoMarkup;

  const parsed = new DOMParser().parseFromString(markup, 'image/svg+xml');

  /*
    A parse failure yields a document whose root is `parsererror`, not `svg`. Returning null lets the
    caller lay out without the mark rather than inserting an error message into the menu, which is the
    right failure for decoration: the menu still works, it is just plainer.
  */
  if (parsed.documentElement.nodeName !== 'svg') return null;

  const svg = document.importNode(parsed.documentElement, true);
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  // Block, so it does not sit on a text baseline and add descender space to its row.
  svg.style.display = 'block';
  svg.style.flex = '0 0 auto';

  /*
    Decorative here: every place this is used sits next to the words "Vibe Flyer", so a screen reader
    announcing the mark as well would just say the name twice.
  */
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  return svg;
}
